import { useEffect } from "react";
import { AppContainer } from "@/components/layout/app-container";
import {
  ServiceProviderHero,
  ProfileCard,
  CompactChartCard,
  RevenueChartCard,
  ActivityLogCard,
  TasksTable,
  CalendarWidget,
} from "@/components/dashboard";
import { useServiceProviderDashboard } from "@/services/queries/useDashboard";
import { RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthContext } from "@/context/authentication/auth-context";

export default function ServiceProviderDashboard() {
  const { person } = useAuthContext();
  const { data, isLoading, error, refetch } = useServiceProviderDashboard();

  // Manual refresh function
  const handleRefresh = () => {
    refetch();
  };

  // Auto-refresh is handled by React Query (configured in the hook)
  useEffect(() => {
    console.log("Service provider dashboard mounted - auto-refresh enabled");
  }, []);

  if (error) {
    return (
      <AppContainer title="Dashboard">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar dashboard</h2>
            <p className="text-gray-400 mb-4">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AppContainer>
    );
  }

  // Extract data with fallback to prevent errors during loading
  const hero = data?.hero || {
    userName: person?.name || "Usuário",
    newMessages: 0,
    hasPendingPayment: false,
  };

  const profile = data?.profile || {
    avatar: "",
    name: person?.name || "Usuário",
    role: "Perfil Prestador de Serviço",
    function: "Prestador de Serviço",
    company: "",
    bio: "",
    socialLinks: {},
  };

  const activities = data?.activities || [];
  const tasks = data?.tasks || [];
  const calendarEvents = data?.calendarEvents || [];
  const invoicesChart = data?.invoicesChart || {
    title: "Notas Fiscais",
    value: "R$0,00",
    percentage: 0,
    period: "Últimos 7 dias",
    chartData: [],
    legend: [],
  };
  const contractsChart = (Array.isArray(data?.contractsChart) ? data?.contractsChart[0] : data?.contractsChart) || {
    title: "Contratos",
    value: "0",
    percentage: 0,
    period: "Total",
    chartData: [],
    legend: [],
  };
  const revenueChart = data?.revenueChart || {
    title: "Notas Fiscais",
    value: "R$0,00",
    percentage: 0,
    period: "Últimos 7 dias",
    data: [],
  };

  return (
    <AppContainer
      title="Dashboard"
      button={
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      }
    >

      {/* Hero Section */}
      <ServiceProviderHero
        userName={hero.userName}
        newMessages={hero.newMessages}
        hasPendingPayment={hero.hasPendingPayment}
      />

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_444px] gap-6 mt-6">
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          {/* Revenue Chart */}
          {isLoading ? (
            <Skeleton className="h-[400px]" />
          ) : (
            <RevenueChartCard
              title={revenueChart.title}
              value={revenueChart.value}
              percentage={revenueChart.percentage}
              period={revenueChart.period}
              data={revenueChart.data}
              isLoading={isLoading}
            />
          )}

          {/* Profile Card */}
          <ProfileCard
            avatar={profile.avatar}
            name={profile.name}
            role={profile.role}
            function={profile.function}
            company={profile.company}
            bio={profile.bio}
            socialLinks={profile.socialLinks}
          />

          {/* Activity Log */}
          <ActivityLogCard activities={activities} />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          {/* Compact Chart 1: Notas Fiscais */}
          <CompactChartCard data={invoicesChart} isLoading={isLoading} />

          {/* Compact Chart 2: Contratos */}
          <CompactChartCard data={contractsChart} isLoading={isLoading} />

          {/* Tasks Table */}
          <TasksTable
            tasks={tasks}
            isLoading={isLoading}
            onViewTask={(id) => console.log("View task:", id)}
            onEditTask={(id) => console.log("Edit task:", id)}
            onDeleteTask={(id) => console.log("Delete task:", id)}
            onToggleTask={(id) => console.log("Toggle task:", id)}
          />

          {/* Calendar Widget */}
          <CalendarWidget
            events={calendarEvents}
            selectedDate={new Date()}
            onDateSelect={(date) => console.log("Selected date:", date)}
          />
        </div>
      </div>
    </AppContainer>
  );
}
